<template>
  <div class="page">
    <Modal
      data-test="modal"
      justifyContent="flex-start"
      textAlign="flex-start"
      :width="isMobile ? '90vw' : '750px'"
      :height="isMobile ? '80vh' : '500px'"
      :show="true"
      :bodyFullHeight="true"
      @close="closeModal"
    >
      <template #header>
        <div class="new-fundraiser-header">
          <div class="new-fundraiser-title">Add to Favorites</div>
        </div>
      </template>

      <div :style="`width: 700px; height: 100%`">
        <Select
          data-test="dropdown"
          v-if="renderDropdown"
          :style="{ width: isMobile ? '100%' : '430px', marginBottom: '16px' }"
          class="inputs"
          id="search_organization"
          data-cy="search_organization"
          :options="
            fundraiserStore.organizations.filter(
              (org) =>
                !favoriteStore.favoriteKeyReference[org.organizations_id] && !selectedOrgKeyRef[org.organizations_id],
            )
          "
          @change="(val) => selectOrg(val.value)"
          optionLabel="organizationName"
          optionValue="organizations_id"
          placeholder="Search Organizations"
          :value="dropdownSelectedValue"
          filter
        >
          Organizations
        </Select>
        <div class="selectedFavoriteOrgsContainer">
          <div
            class="flex gap-6 items-center selectedFavoriteOrg mb-4"
            v-for="(org, index) in selectedOrgs"
            :key="`selectedOrg_${index}`"
          >
            <div>
              <div class="organization-image">
                <img
                  :src="`https://ddb9l06w3jzip.cloudfront.net/uploadedFiles/giving_affiliate/organizations/logo/${org.organizations_id}`"
                  alt="Hero Image"
                />
              </div>
            </div>
            <div class="flex-1 font-bold">
              {{ org.organizationName }}
            </div>
            <div>
              <Trashcan
                class="cursor-pointer"
                @click="removeSelectedOrg(index)"
              />
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <Button
          data-test="cancel-button"
          label="Cancel"
          variant="link"
          :style="`color: ${donorStore.mainColor}`"
          @click="closeModal"
        />
        <Button
          data-test="add-button"
          label="Add"
          :style="`width: 100px;color: ${getContrastYIQ(donorStore.mainColor)}`"
          @click="addOrgsToFavorites"
          :loading="addLoading"
        />
      </template>
    </Modal>
  </div>
</template>

<script setup>
import { ref, computed, onBeforeMount, watch, defineEmits, nextTick } from 'vue';
import { useDonorStore } from '@/stores/DonorStore.js';
import { useFundraiserStore } from '@/stores/FundraiserStore.js';
import { useFavoriteStore } from '@/stores/FavoriteStore.js';
import Modal from '@/components/Modal.vue';
import Trashcan from '@/assets/Trashcan.vue';
import getContrastYIQ from '@/helpers/getContrastYIQ';
import useIsMobile from '@/composables/useIsMobile.js';

const { isMobile } = useIsMobile();

// Accept the dropdownSelectedValue prop
const props = defineProps({
  dropdownSelectedValue: {
    type: [String, Number, Object],
    default: null,
  },
});

const emit = defineEmits(['close']);

const donorStore = useDonorStore();
const fundraiserStore = useFundraiserStore();
const favoriteStore = useFavoriteStore();
const mainColor = donorStore.mainColor;

const addAFavoriteDialog = ref(true);

const selectedOrgs = ref([]);

const addLoading = ref(false);
const renderDropdown = ref(true);

const selectOrg = async (orgId) => {
  selectedOrgs.value.push(fundraiserStore.organizations.find((org) => orgId == org.organizations_id));
  // This is a very dirty hack to force the dropdown to re-render/clear typed in value. We will want to avoid this once we implement a new component library
  await nextTick();
  renderDropdown.value = false;
  await nextTick();
  renderDropdown.value = true;
};

const removeSelectedOrg = (index) => {
  selectedOrgs.value.splice(index, 1);
};

const selectedOrgKeyRef = computed(() => {
  let orgs = {};
  selectedOrgs.value.forEach((org) => {
    orgs[org.organizations_id] = true;
  });
  return orgs;
});

const addOrgsToFavorites = async () => {
  addLoading.value = true;
  await favoriteStore.addOrgsToFavorites(
    selectedOrgs.value.map((org) => {
      return {
        organizations_id: org.organizations_id,
        donorAccounts_id: donorStore.donorAccounts_id,
      };
    }),
  );
  selectedOrgs.value = [];
  addLoading.value = false;
  closeModal();
};

const closeModal = () => {
  fundraiserStore.resetData(); // Reset data when closing the modal
  emit('close');
};

onBeforeMount(async () => {
  await fundraiserStore.bringAllOrganizations();
});
</script>
<style lang="scss" scoped>
.new-fundraiser-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  top: 24px;
  left: 48px;
  width: 600px;
}

.new-fundraiser-title {
  font-size: 2.75rem;
  font-weight: bold;
  color: var(--p-primary-500);
}

.current-step {
  font-size: 1rem;
  font-weight: 400;
}

.selectedFavoriteOrgsContainer {
  max-width: 430px;
}

.selectedFavoriteOrg {
  border-radius: 8px;
  border: 1px solid #e6e9ee;
  padding: 12px;
}

.selectedFavoriteOrg .organization-image {
  width: 58px;
  border-radius: 0;
}

.organization-image img {
  width: 100%; /* Ensure image takes full width */
  height: 100%; /* Ensure image takes full height */
  object-fit: contain; /* Cover the container without being clipped */
}

@media screen and (max-width: 768px) {
  .new-fundraiser-header {
    flex-direction: column;
    position: static;
  }
  .new-fundraiser-title {
    font-size: 1.875rem;
  }
}
</style>
